/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { arrayOf, date, DateFormat, entity, enumeration, subentity } from '@gh/core-data';
import {
  ScheduleDetailsDto,
  ScheduleDetailsTaskDto,
  ScheduleTaskCcResourceListItemDto,
  ScheduleTaskChangeDateAfterCompleteType,
  ScheduleTaskRelatedResourceTaskListItemDto,
  ScheduleTaskTodoEventType,
  ScheduleTaskType,
  ScheduleTaskUserResourceListItemDto,
  ScheduleType,
  TaskRelationDto,
  TaskRelationType,
} from '../schedule-templates';
import { JobContractDaysPeriodType } from '../system-vars';
import { UserSystemRoleIntMapper } from '../users';
import {
  ConstructionWorksheetDto,
  ConstructionWorksheetScheduleTaskDto,
  JobScheduleListItemDto,
  PreConstructionWorksheetDto,
  PreConstructionWorksheetSaveDto,
  PreConstructionWorksheetScheduleDto,
  PreConstructionWorksheetScheduleSaveDto,
  PreConstructionWorksheetScheduleTaskDto,
  PreConstructionWorksheetScheduleTaskSaveDto,
  PreConstructionWorksheetScheduleTemplateTaskDto,
  SalesScheduleListItemDto,
  ScheduleSupervisorReportRequestDto,
  ScheduleSupervisorTaskFilterDto,
  ScheduleTaskDetailsDto,
  ScheduleTaskNoteListItemDto,
  ScheduleTaskTodoListItemDto,
  ScheduleTaskTodoUserDto,
  ScheduleVendorReportRequestDto,
} from './schedule.dto';

export const JobScheduleListItemMapper = entity<JobScheduleListItemDto>({
  startDate: date(DateFormat.DateTime),
  endDate: date(DateFormat.DateTime),
});

export const SalesScheduleListItemMapper = entity<SalesScheduleListItemDto>({
  startDate: date(DateFormat.DateTime),
  endDate: date(DateFormat.DateTime),
});

export const ConstructionScheduleListItemMapper = JobScheduleListItemMapper;
export const PreConstructionScheduleListItemMapper = JobScheduleListItemMapper;
export const MaintenanceScheduleListItemMapper = JobScheduleListItemMapper;

export const ScheduleDetailsMapper = entity<ScheduleDetailsDto>({
  type: enumeration(ScheduleType),
  salesFolderAddedDate: date(DateFormat.DateTime),
  startDate: date(DateFormat.DateTime),
  endDate: date(DateFormat.DateTime),
  baseStartDate: date(DateFormat.DateTime),
  baseEndDate: date(DateFormat.DateTime),
  jobContractConstructionPeriodIncludes: enumeration(JobContractDaysPeriodType),
  tasks: arrayOf(subentity<ScheduleDetailsTaskDto>({
    type: enumeration(ScheduleTaskType),
    startDate: date(DateFormat.DateTime),
    endDate: date(DateFormat.DateTime),
    baseStartDate: date(DateFormat.DateTime),
    baseEndDate: date(DateFormat.DateTime),
    completionDate: date(DateFormat.Date),
    confirmedOnDate: date(DateFormat.Date),
    confirmedForDate: date(DateFormat.Date),
    predecessors: arrayOf(subentity<TaskRelationDto>({
      relation: enumeration(TaskRelationType),
    })),
  })),
});

export const ScheduleTaskDetailsMapper = entity<ScheduleTaskDetailsDto>({
  type: enumeration(ScheduleTaskType),
  startDate: date(DateFormat.DateTime),
  endDate: date(DateFormat.DateTime),
  baseStartDate: date(DateFormat.DateTime),
  baseEndDate: date(DateFormat.DateTime),
  confirmedOnDate: date(DateFormat.Date),
  confirmedForDate: date(DateFormat.Date),
  completionDate: date(DateFormat.Date),
  predecessors: arrayOf(subentity<TaskRelationDto>({
    relation: enumeration(TaskRelationType),
  })),
  ccResources: arrayOf(subentity<ScheduleTaskCcResourceListItemDto>({
    currentTasks: arrayOf(subentity<ScheduleTaskRelatedResourceTaskListItemDto>({
      type: enumeration(ScheduleTaskType),
      startDate: date(DateFormat.DateTime),
      endDate: date(DateFormat.DateTime),
    })),
  })),
  employeeResources: arrayOf(subentity<ScheduleTaskUserResourceListItemDto>({
    currentTasks: arrayOf(subentity<ScheduleTaskRelatedResourceTaskListItemDto>({
      type: enumeration(ScheduleTaskType),
      startDate: date(DateFormat.DateTime),
      endDate: date(DateFormat.DateTime),
    })),
  })),
  changeDateAfterComplete: enumeration(ScheduleTaskChangeDateAfterCompleteType),
  todo: arrayOf(subentity<ScheduleTaskTodoListItemDto>({
    event: enumeration(ScheduleTaskTodoEventType),
    addedDate: date(DateFormat.DateTime),
    user: subentity<ScheduleTaskTodoUserDto>({}),
    systemRole: UserSystemRoleIntMapper,
  })),
  notes: arrayOf(subentity<ScheduleTaskNoteListItemDto>({
    addedDate: date(DateFormat.DateTime),
  })),
});

export const ScheduleSupervisorTaskFilterMapper = entity<ScheduleSupervisorTaskFilterDto>({
  startDate: date(DateFormat.DateTime),
  endDate: date(DateFormat.DateTime),
});

export const ScheduleSupervisorReportRequestMapper = entity<ScheduleSupervisorReportRequestDto>({
  startDate: date(DateFormat.DateTime),
  endDate: date(DateFormat.DateTime),
});

export const ScheduleVendorReportRequestMapper = entity<ScheduleVendorReportRequestDto>({
  from: date(DateFormat.Date),
  to: date(DateFormat.Date),
});

export const PreConstructionWorksheetMapper = entity<PreConstructionWorksheetDto>({
  schedules: arrayOf(subentity<PreConstructionWorksheetScheduleDto>({
    rfqProducedDate: date(DateFormat.DateTime),
    rfqAuthoritySignedDate: date(DateFormat.Date),
    slabDate: date(DateFormat.Date),
    scheduleTasks: arrayOf(subentity<PreConstructionWorksheetScheduleTaskDto>({
      endDate: date(DateFormat.DateTime),
      completionDate: date(DateFormat.Date),
    })),
  })),
  templateTasks: arrayOf(subentity<PreConstructionWorksheetScheduleTemplateTaskDto>({
    type: enumeration(ScheduleTaskType),
  })),
});

export const PreConstructionWorksheetSaveMapper = entity<PreConstructionWorksheetSaveDto>({
  schedules: arrayOf(subentity<PreConstructionWorksheetScheduleSaveDto>({
    rfqAuthoritySignedDate: date(DateFormat.Date),
    scheduleTasks: arrayOf(subentity<PreConstructionWorksheetScheduleTaskSaveDto>({
      completionDate: date(DateFormat.Date),
    })),
  })),
});

export const ConstructionWorksheetMapper = entity<ConstructionWorksheetDto>({
  tasks: arrayOf(subentity<ConstructionWorksheetScheduleTaskDto>({
    type: enumeration(ScheduleTaskType),
    startDate: date(DateFormat.DateTime),
    completionDate: date(DateFormat.Date),
    confirmedOnDate: date(DateFormat.Date),
    confirmedForDate: date(DateFormat.Date),
  })),
});
